import { appContext } from "../../../../AppContext";
import { JSX } from "react";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { Dialog, DialogContent, DialogFooter, IRouterProps, MultilingualTextField, withRouting } from "@iconics/components";
import { ITreeViewRule, IOntology, VirtualTreeView, TreeViewRulesDefinition, IOntologyNode, ITreeViewRulesData } from "./DataModels";
import { PagedResponse, TypeHelper } from "@iconics/core";
import { ApiConstants } from "@iconics/core";
import { SerendieSymbol } from "@serendie/symbols";
import { ILanguageValues, LanguageValuesHelper } from "@iconics/localisation";
import { Switch } from "@serendie/ui";
import { Validator } from '@iconics/core';
import { Button, ButtonBar, Card, DeleteButton, GridContainer, GridItem, IPageNavSection, LayoutContainer, LayoutContent, LayoutFooter, LayoutRight, LeftButtons, PageNav, RightButtons, Select, TextArea, TextField } from "@iconics/components";
import { IVirtualTreeViewDefinition } from "@iconics/providers.fadx-platform-api-client-v2";
import VirtualTreeViewRulesEditor from "./VirtualTreeViewRulesEditor";

class EditVirtualTreeView extends IbssPage<IProps, IState>
{
    private validator = new Validator(appContext().core.validationOptions);
    private labels = appContext().labels;
    private get isNew() { return !this.props.params.id; }
    private languageValuesHelper: LanguageValuesHelper;
    private definitions: { value: string, label: string }[] = [
        { value: 'TreeViewRules', label: this.labels.VirtualTreeView_Definitions_TreeViewRules }
    ];

    constructor(props: IProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);

        this.state = {
            isEnabled: false,
            name: '',
            nameError: '',
            displayName: this.languageValuesHelper.default(),
            description: '',
            descriptionError: '',
            definition: {},
            definitionError: '',
            ontologies: [],
            ontologyNodes: [],
            ontologyId: '',
            ontologyIdError: '',
            eTag: '',
            canUpdate: appContext().localStorageProvider.hasRight('DATAMODEL.VirtualTreeView.Update'),
            canDelete: appContext().localStorageProvider.hasRight('DATAMODEL.VirtualTreeView.Delete'),
            sections: [],
            showAlert: false,
            alertMessage: '',
            alertResolver: null
        };
    }

    public async componentDidMount(): Promise<void>
    {
        const labels = appContext().labels;
        this.pageTitle = labels.Platform_VirtualTreeView_PageTitle;
        this.backUrl = `/admin/virtual-tree-views`;
        this.validator
            .addInput('name', () => this.state.name, i => this.setState({ nameError: i }))
            .isRequired();
        this.validator
            .addInput('description', () => this.state.description, i => this.setState({ descriptionError: i }))
            .isRequired();
        this.validator
            .addInput('ontologyId', () => this.state.ontologyId, i => this.setState({ ontologyIdError: i }))
            .isRequired();
        this.validator
            .addInput('definition', () => this.state.definition?.Model ?? '', i => this.setState({ definitionError: i }))
            .isRequired();

        await this.loadOntologies();

        if (!this.isNew)
        {
            await this.loading(async () =>
            {
                const virtualTreeView = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.virtualTreeViews.byId.get<VirtualTreeView>({
                    id: this.props.params.id ?? '',
                    select: VirtualTreeView,
                    tenancy: appContext().tenantId,
                });

                this.setState({
                    isEnabled: virtualTreeView.IsEnabled,
                    name: virtualTreeView.Name,
                    displayName: this.languageValuesHelper.fromString(virtualTreeView.DisplayName),
                    description: virtualTreeView.Description,
                    definition: virtualTreeView.Definition,
                    ontologyId: virtualTreeView.OntologyId,
                    eTag: virtualTreeView['@odata.etag'],
                });

                this.handleOntologyChange(virtualTreeView.OntologyId, true);
            });
        }
    }

    private async loadOntologies(): Promise<void>
    {
        try
        {
            const ontologies = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.get<PagedResponse<IOntology[]>>({
                select: 'Name,IsEnabled,ID',
                tenancy: appContext().tenantId,
                top: ApiConstants.largeODataTop,
            });
            this.setState({ ontologies: ontologies.value });
        }
        catch (error)
        {
        }
    }

    private async handleDelete(): Promise<void>
    {
        await this.loading(async () =>
        {
            const id = TypeHelper.toNumber(this.props.params.id);
            if (!id)
            {
                return;
            }

            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.virtualTreeViews.byId.delete({
                id: id,
                tenancy: appContext().tenantId,
            });
            this.props.navigate('/admin/virtual-tree-views');
        });
    }

    private async handleSave(): Promise<void>
    {
        if (!this.validator.validateAll())
        {
            return;
        }
        await this.loading(async () =>
        {
            if (!this.isNew)
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.virtualTreeViews.byId.patch({
                    body: {
                        IsEnabled: this.state.isEnabled ? 'Yes' : 'No',
                        Name: this.state.name,
                        DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                        Description: this.state.description,
                        Definition: this.state.definition,
                        OntologyId: this.state.ontologyId,
                        '@odata.etag': this.state.eTag,
                    },
                    id: Number.parseInt(this.props.params.id ?? ''),
                    tenancy: appContext().tenantId,
                });
            }
            else
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.virtualTreeViews.post({
                    body: {
                        IsEnabled: this.state.isEnabled ? 'Yes' : 'No',
                        Name: this.state.name,
                        DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                        Description: this.state.description,
                        Definition: this.state.definition,
                        OntologyId: this.state.ontologyId,
                    },
                    tenancy: appContext().tenantId,
                });
            }
            this.props.navigate('/admin/virtual-tree-views');
        });
    }

    private handleAlertClose(result: boolean): void
    {
        this.setState({ showAlert: false });
        if (this.state.alertResolver)
            this.state.alertResolver(result);
    }

    private hasRules(): boolean
    {
        const data = this.state.definition?.Data as ITreeViewRulesData | undefined;
        return (data?.TreeViewRules?.length ?? 0) > 0;
    }

    private async handleOntologyChange(value: string, firstLoad = false): Promise<void>
    {
        if (!firstLoad && this.hasRules())
        {
            const userConfirmed = await new Promise<boolean>((resolve) =>
            {
                this.setState({
                    alertResolver: resolve,
                    showAlert: true,
                    alertMessage: this.labels.VirtualTreeView_WarnChangeOntology
                });
            });

            if (!userConfirmed)
            {
                return;
            }
        }

        let { ontologyNodes } = this.state;
        if (value)
        {
            try
            {
                const ontologyNodesResponse = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.byId.nodes.get<PagedResponse<IOntologyNode[]>>({
                    tenancy: appContext().tenantId,
                    id: value
                });
                ontologyNodes = ontologyNodesResponse.value;
            }
            catch (error)
            {
            }
        }
        if (firstLoad)
        {
            this.setState({ ontologyId: value, ontologyNodes });
            return;
        }

        // reset the rules, as the ontology nodes have changed
        if (this.state.definition?.Model)
            await this.handleDefinitionModelChange(this.state.definition.Model, true);
        this.setState({ ontologyId: value, ontologyNodes }, () => this.validator.validate('ontologyId'));
    }

    private async handleDefinitionModelChange(value: string, firstLoad = false): Promise<void>
    {
        if (!firstLoad && this.hasRules())
        {
            const userConfirmed = await new Promise<boolean>((resolve) =>
            {
                this.setState({
                    alertResolver: resolve,
                    showAlert: true,
                    alertMessage: this.labels.VirtualTreeView_WarnChangeDefinition
                });
            });

            if (!userConfirmed)
            {
                return;
            }
        }

        let { definition } = this.state;
        switch (value)
        {
            case 'TreeViewRules':
                definition = new TreeViewRulesDefinition();
                break;
            default:
                break;
        }
        this.setState({ definition }, () => this.validator.validate('definition'));
    }

    private onDefinitionUpdate(rules: ITreeViewRule[]): void
    {
        const definition = new TreeViewRulesDefinition();
        definition.Data = { TreeViewRules: rules };
        this.setState({
            definition
        });
    }

    private renderDefinitionEditor(): JSX.Element
    {
        const { definition, ontologyNodes } = this.state;
        if (definition as TreeViewRulesDefinition && definition.Data)
            return (
                <VirtualTreeViewRulesEditor
                    rules={definition.Data}
                    ontologyNodes={ontologyNodes}
                    onDefinitionUpdate={(rules) => this.onDefinitionUpdate(rules)}
                    onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 1) }))}
                />
            );
        // we only support TreeViewRulesDefinition so far
        return (
            <></>
        );
    }

    public render(): JSX.Element
    {
        return (
            <>
                <LayoutContainer>
                    <LayoutContent>
                        <Card
                            title={this.labels.Platform_VirtualTreeView_DetailsTitle}
                            subTitle={this.labels.Platform_VirtualTreeView_DetailsSubTitle}
                            onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 0) }))}
                            icon={<SerendieSymbol name='layout-row' />}
                        >
                            <GridContainer>
                                <GridItem expanded={6}>
                                    <TextField
                                        required
                                        label={this.labels.VirtualTreeView_Name}
                                        placeholder={this.labels.VirtualTreeView_Name}
                                        value={this.state.name}
                                        onChange={e => this.setState({ name: e.target.value }, () => this.validator.validate('name'))}
                                        invalid={!!this.state.nameError}
                                        invalidMessage={this.state.nameError}
                                    />
                                </GridItem>
                                <GridItem expanded={6}>
                                    <MultilingualTextField
                                        label={this.labels.VirtualTreeView_DisplayName}
                                        placeholder={this.labels.VirtualTreeView_DisplayName}
                                        value={this.state.displayName}
                                        onChange={value => this.setState({ displayName: value })}
                                        localisation={appContext().localisation}
                                    />
                                </GridItem>
                                <GridItem expanded={6}>
                                    <TextField
                                        required
                                        label={this.labels.VirtualTreeView_Description}
                                        placeholder={this.labels.VirtualTreeView_Description}
                                        value={this.state.description}
                                        onChange={e => this.setState({ description: e.target.value }, () => this.validator.validate('description'))}
                                        invalid={!!this.state.descriptionError}
                                        invalidMessage={this.state.descriptionError}
                                    />
                                </GridItem>
                                <GridItem expanded={6}>
                                    <Select
                                        label={this.labels.VirtualTreeView_OntologyId}
                                        placeholder={this.labels.VirtualTreeView_OntologyId}
                                        value={this.state.ontologyId ? [this.state.ontologyId] : []}
                                        onSelect={e => this.handleOntologyChange(e.value)}
                                        items={this.state.ontologies.filter(x => x.IsEnabled === 'Yes').map(x => ({ label: x.Name, value: x.Id }))}
                                        required
                                        invalid={!!this.state.ontologyIdError}
                                        invalidMessage={this.state.ontologyIdError}
                                    />
                                </GridItem>
                                <GridItem>
                                    <Switch
                                        label={this.labels.VirtualTreeView_IsEnabled}
                                        checked={this.state.isEnabled}
                                        onCheckedChange={e => this.setState({ isEnabled: e.checked as boolean })}
                                    />
                                </GridItem>
                                <GridItem>
                                    <Select
                                        label={this.labels.VirtualTreeView_Definition}
                                        placeholder={this.labels.VirtualTreeView_Definition}
                                        value={this.state.definition?.Model ? [this.state.definition.Model] : []}
                                        onSelect={e => this.handleDefinitionModelChange(e.value)}
                                        items={this.definitions}
                                        required
                                        invalid={!!this.state.definitionError}
                                        invalidMessage={this.state.definitionError}
                                    />
                                </GridItem>
                                <GridItem>
                                    {this.renderDefinitionEditor()}
                                </GridItem>
                            </GridContainer>
                        </Card>
                    </LayoutContent>
                    <LayoutRight>
                        <PageNav
                            sections={this.state.sections}
                        />
                    </LayoutRight>
                    <LayoutFooter>
                        <ButtonBar>
                            <LeftButtons>
                                {
                                    !this.isNew &&
                                    <DeleteButton
                                        disabled={!this.state.canDelete}
                                        dialogMessage={this.labels.Platform_VirtualTreeView_ConfirmDeleteMsg}
                                        onDelete={() => this.handleDelete()}
                                    />
                                }
                            </LeftButtons>
                            <RightButtons>
                                <Button
                                    styleType='outlined'
                                    onClick={() => this.props.navigate('/admin/virtual-tree-views')}
                                >
                                    {this.labels.Platform_CommonAction_Cancel}
                                </Button>
                                <Button
                                    styleType='filled'
                                    onClick={() => this.handleSave()}
                                    disabled={!this.state.canUpdate || !this.validator.isValid}
                                >
                                    {this.labels.Platform_CommonAction_Save}
                                </Button>
                            </RightButtons>
                        </ButtonBar>
                    </LayoutFooter>
                </LayoutContainer>
                <Dialog
                    isOpen={this.state.showAlert}
                    onOpenChange={() => this.handleAlertClose(false)}
                >
                    <DialogContent>
                        <Card
                            title={this.labels.Platform_Common_Attention}
                        >
                            {this.state.alertMessage}
                        </Card>
                    </DialogContent>
                    <DialogFooter>
                        <ButtonBar>
                            <RightButtons>
                                <Button
                                    styleType={'outlined'}
                                    onClick={() => this.handleAlertClose(false)}
                                >
                                    {this.labels.Platform_CommonAction_Cancel}
                                </Button>
                                <Button
                                    onClick={() => this.handleAlertClose(true)}
                                >
                                    {this.labels.Platform_CommonAction_Ok}
                                </Button>
                            </RightButtons>
                        </ButtonBar>
                    </DialogFooter>
                </Dialog>
            </>
        );
    }
}

export default withRouting(EditVirtualTreeView);

export interface IProps extends IRouterProps<IRouteParams>
{
}

export interface IRouteParams
{
    id?: string;
}

export interface IState
{
    isEnabled: boolean;
    name: string;
    nameError: string;
    displayName: ILanguageValues;
    description: string;
    descriptionError: string;
    definition: IVirtualTreeViewDefinition;
    definitionError: string;
    ontologies: IOntology[];
    ontologyNodes: IOntologyNode[];
    ontologyId: string;
    ontologyIdError: string;
    eTag: string;
    canUpdate: boolean;
    canDelete: boolean;
    sections: IPageNavSection[];
    showAlert: boolean;
    alertMessage: string;
    alertResolver: ((value: boolean) => void) | null;
}
